import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const Telecommunications = () => {
  return (
    <>
      <SEO 
        title="Telecommunications Solutions - AI & Digital Transformation"
        description="Transform your telecommunications operations with AI-powered network optimization, customer experience solutions, and digital infrastructure modernization. Drive innovation and efficiency in the telecom industry."
        keywords="telecommunications AI, telecom solutions, network optimization, 5G implementation, customer experience telecom, digital transformation telecom, network analytics, OSS BSS solutions"
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blush-light/30 rounded-full mb-4">
              <span className="text-primary-red font-semibold">Industry Solutions</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Telecommunications <span className="text-gradient">Solutions</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed animate-slide-up mb-8">
              Empower your telecommunications business with AI-driven network optimization, intelligent customer experience platforms, and advanced analytics for the 5G era and beyond.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Schedule Consultation
              </Link>
              <Link to="/services" className="btn-secondary">
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Challenges */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-title">Telecommunications Industry Challenges</h2>
            <p className="section-subtitle">
              Navigate the complex landscape of modern telecommunications with intelligent solutions
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-charcoal">5G Network Deployment</h3>
              <p className="text-titanium">
                Complex infrastructure upgrades, spectrum management, and rapid deployment requirements for next-generation networks.
              </p>
            </div>

            <div className="card">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-charcoal">Revenue Pressure</h3>
              <p className="text-titanium">
                Declining ARPU, intense competition, and need for new revenue streams through digital services and partnerships.
              </p>
            </div>

            <div className="card">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-charcoal">Network Security</h3>
              <p className="text-titanium">
                Protecting critical infrastructure from cyber threats, DDoS attacks, and ensuring compliance with security regulations.
              </p>
            </div>

            <div className="card">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-charcoal">Customer Churn</h3>
              <p className="text-titanium">
                High churn rates requiring predictive analytics, personalized retention strategies, and improved customer experience.
              </p>
            </div>

            <div className="card">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-charcoal">Legacy System Integration</h3>
              <p className="text-titanium">
                Modernizing OSS/BSS systems while maintaining service continuity and integrating with cloud-native technologies.
              </p>
            </div>

            <div className="card">
              <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-charcoal">Network Performance</h3>
              <p className="text-titanium">
                Optimizing network capacity, reducing latency, and ensuring QoS across increasingly complex multi-vendor environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Solutions */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-title">Our Telecommunications Solutions</h2>
            <p className="section-subtitle">
              Comprehensive technology solutions designed specifically for telecommunications providers
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="card hover:shadow-xl transition-all duration-300">
              <div className="flex items-start mb-4">
                <div className="w-14 h-14 bg-primary-red/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-charcoal">AI-Powered Network Optimization</h3>
                  <p className="text-titanium mb-4">
                    Leverage machine learning to optimize network performance, predict capacity needs, and automate resource allocation across your infrastructure.
                  </p>
                  <ul className="space-y-2 text-titanium">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Self-optimizing network (SON) capabilities
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Predictive network maintenance and fault detection
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Real-time traffic analysis and routing optimization
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Spectrum efficiency and interference management
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card hover:shadow-xl transition-all duration-300">
              <div className="flex items-start mb-4">
                <div className="w-14 h-14 bg-primary-red/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-charcoal">Customer Experience Transformation</h3>
                  <p className="text-titanium mb-4">
                    Deliver personalized, omnichannel experiences that reduce churn and increase customer lifetime value through AI-driven insights.
                  </p>
                  <ul className="space-y-2 text-titanium">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      AI-powered chatbots and virtual assistants
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Predictive churn analytics and retention campaigns
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      360-degree customer view and journey analytics
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Personalized offer and recommendation engines
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card hover:shadow-xl transition-all duration-300">
              <div className="flex items-start mb-4">
                <div className="w-14 h-14 bg-primary-red/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-charcoal">Cloud-Native BSS/OSS Modernization</h3>
                  <p className="text-titanium mb-4">
                    Transform legacy systems with cloud-native, microservices-based architecture for greater agility and reduced operational costs.
                  </p>
                  <ul className="space-y-2 text-titanium">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Automated billing and revenue management
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Service orchestration and fulfillment automation
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      API-first architecture for partner integration
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Scalable, containerized deployment models
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card hover:shadow-xl transition-all duration-300">
              <div className="flex items-start mb-4">
                <div className="w-14 h-14 bg-primary-red/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-charcoal">Advanced Security & Fraud Detection</h3>
                  <p className="text-titanium mb-4">
                    Protect your network and customers with AI-powered security solutions that detect and prevent threats in real-time.
                  </p>
                  <ul className="space-y-2 text-titanium">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Real-time fraud detection and prevention
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      DDoS protection and threat intelligence
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Network security monitoring and incident response
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Compliance automation and reporting
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-title">Key Benefits</h2>
            <p className="section-subtitle">
              Measurable impact on your telecommunications operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="text-3xl font-bold text-primary-red">35%</div>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-charcoal">OPEX Reduction</h3>
              <p className="text-titanium">Through automated network optimization and maintenance</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="text-3xl font-bold text-primary-red">40%</div>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-charcoal">Churn Reduction</h3>
              <p className="text-titanium">With predictive analytics and personalized retention</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="text-3xl font-bold text-primary-red">50%</div>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-charcoal">Faster Resolution</h3>
              <p className="text-titanium">Network issues resolved proactively before customer impact</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="text-3xl font-bold text-primary-red">25%</div>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-charcoal">Revenue Growth</h3>
              <p className="text-titanium">From new digital services and improved customer experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="card bg-white">
              <div className="inline-block px-4 py-2 bg-primary-red/10 rounded-full mb-4">
                <span className="text-primary-red font-semibold">Success Story</span>
              </div>
              <h2 className="text-3xl font-bold mb-6 text-charcoal">
                Leading Mobile Operator Transforms Network Operations
              </h2>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div>
                  <div className="text-3xl font-bold text-primary-red mb-2">45%</div>
                  <p className="text-titanium">Reduction in network downtime</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary-red mb-2">$12M</div>
                  <p className="text-titanium">Annual operational savings</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary-red mb-2">92%</div>
                  <p className="text-titanium">Customer satisfaction score</p>
                </div>
              </div>
              <p className="text-titanium mb-4">
                A major telecommunications provider partnered with Abacies to implement AI-powered network optimization and predictive maintenance across their 5G infrastructure. The solution integrated real-time analytics with automated remediation, resulting in significant improvements in network reliability and customer experience.
              </p>
              <p className="text-titanium mb-6">
                By leveraging machine learning models for capacity planning and fault prediction, the operator reduced unplanned outages by 45% and optimized capital expenditure on network expansion. The implementation also enabled proactive customer communication during service events, improving satisfaction scores by 28 points.
              </p>
              <Link to="/contact" className="btn-primary">
                Discuss Your Project
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-title">Technology Excellence</h2>
            <p className="section-subtitle">
              Built on industry-leading platforms and frameworks
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card text-center">
              <h3 className="text-xl font-semibold mb-4 text-charcoal">Network Intelligence</h3>
              <div className="space-y-2 text-titanium">
                <p>AI/ML for Network Optimization</p>
                <p>Real-time Analytics Platforms</p>
                <p>SDN/NFV Technologies</p>
                <p>Edge Computing Integration</p>
              </div>
            </div>

            <div className="card text-center">
              <h3 className="text-xl font-semibold mb-4 text-charcoal">BSS/OSS Platforms</h3>
              <div className="space-y-2 text-titanium">
                <p>Cloud-Native Microservices</p>
                <p>API Management & Integration</p>
                <p>DevOps & CI/CD Pipelines</p>
                <p>Containerization (Kubernetes)</p>
              </div>
            </div>

            <div className="card text-center">
              <h3 className="text-xl font-semibold mb-4 text-charcoal">Customer Solutions</h3>
              <div className="space-y-2 text-titanium">
                <p>Omnichannel Platforms</p>
                <p>Advanced Analytics & BI</p>
                <p>AI-Powered Personalization</p>
                <p>Real-time Decision Engines</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary-red text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Telecommunications Operations?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Partner with Abacies to implement cutting-edge AI and digital solutions that drive operational excellence and customer satisfaction in the 5G era.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Consultation
            </Link>
            <Link to="/services/ai-machine-learning" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Explore AI Solutions
            </Link>
          </div>
        </div>
      </section>

      {/* Related Solutions */}
      <section className="py-16 bg-alabaster">
        <div className="page-container">
          <h2 className="text-2xl font-bold mb-8 text-charcoal text-center">Related Industry Solutions</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/solutions/technology-software" className="card hover:shadow-lg transition-all duration-300">
              <h3 className="font-semibold text-charcoal mb-2">Technology & Software</h3>
              <p className="text-sm text-titanium">Digital transformation for tech companies</p>
            </Link>
            <Link to="/solutions/media-entertainment" className="card hover:shadow-lg transition-all duration-300">
              <h3 className="font-semibold text-charcoal mb-2">Media & Entertainment</h3>
              <p className="text-sm text-titanium">Content delivery and streaming solutions</p>
            </Link>
            <Link to="/solutions/energy-utilities" className="card hover:shadow-lg transition-all duration-300">
              <h3 className="font-semibold text-charcoal mb-2">Energy & Utilities</h3>
              <p className="text-sm text-titanium">Infrastructure modernization and IoT</p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Telecommunications;